// ─────────────────────────────────────────────────────────────────────────────
// KnowledgeBase Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, helpers, and reducer for the knowledgeBase
// store slice.
// ─────────────────────────────────────────────────────────────────────────────

import { createReducer, on } from '@ngrx/store';
import { IKbDocument, KbDocumentType } from '../models/knowledge-base.models';
import * as KbActions from './knowledge-base.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface KnowledgeBaseState {
  documents: IKbDocument[];
  activeTypeFilter: KbDocumentType | 'all';
  searchQuery: string;
  sortKey: string;
  sortMode: 'asc' | 'desc';
  expandedDocumentIds: Record<string, boolean>;
  loading: {
    documents: boolean;
    upload: boolean;
  };
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialKnowledgeBaseState: KnowledgeBaseState = {
  documents: [],
  activeTypeFilter: 'all',
  searchQuery: '',
  sortKey: 'upload_date',
  sortMode: 'desc',
  expandedDocumentIds: {},
  loading: {
    documents: false,
    upload: false,
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function toggleExpanded(map: Record<string, boolean>, id: string): Record<string, boolean> {
  const next = { ...map };
  if (next[id]) {
    delete next[id];
  } else {
    next[id] = true;
  }
  return next;
}

function updateDocumentInList(documents: IKbDocument[], updated: IKbDocument): IKbDocument[] {
  return documents.map((doc) => (doc.id === updated.id ? updated : doc));
}

// ── Reducer ──────────────────────────────────────────────────────────────────

export const knowledgeBaseReducer = createReducer(
  initialKnowledgeBaseState,

  // Load documents
  on(KbActions.LoadDocuments, (state) => ({
    ...state,
    loading: { ...state.loading, documents: true },
  })),
  on(KbActions.LoadDocumentsSuccess, (state, { documents }) => ({
    ...state,
    documents,
    loading: { ...state.loading, documents: false },
  })),
  on(KbActions.LoadDocumentsError, (state) => ({
    ...state,
    loading: { ...state.loading, documents: false },
  })),

  // Filter & search
  on(KbActions.SetTypeFilter, (state, { filterType }) => ({
    ...state,
    activeTypeFilter: filterType,
  })),
  on(KbActions.SetSearchQuery, (state, { query }) => ({
    ...state,
    searchQuery: query,
  })),
  on(KbActions.SetSort, (state, { sortKey, sortMode }) => ({
    ...state,
    sortKey,
    sortMode,
  })),

  // Upload
  on(KbActions.UploadDocument, (state) => ({
    ...state,
    loading: { ...state.loading, upload: true },
  })),
  on(KbActions.UploadDocumentSuccess, (state, { document }) => ({
    ...state,
    documents: [document, ...state.documents],
    loading: { ...state.loading, upload: false },
  })),
  on(KbActions.UploadDocumentError, (state) => ({
    ...state,
    loading: { ...state.loading, upload: false },
  })),
  on(KbActions.UpdateDocumentSuccess, (state, { document }) => ({
    ...state,
    documents: updateDocumentInList(state.documents, document),
    loading: { ...state.loading, upload: false },
  })),

  // Delete (optimistic removal from list on success)
  on(KbActions.DeleteDocumentsSuccess, (state, { documentIds }) => ({
    ...state,
    documents: state.documents.filter((doc) => !documentIds.includes(doc.id)),
  })),

  // Toggle expanded row
  on(KbActions.ToggleDocumentExpanded, (state, { documentId }) => ({
    ...state,
    expandedDocumentIds: toggleExpanded(state.expandedDocumentIds, documentId),
  })),
);
